/**
 * Isolated access to Angular SSR's DOM emulation (domino).
 *
 * IMPORTANT: `ɵDominoAdapter` is a private API of @angular/platform-server
 * (Angular vendors domino inside the package; the project has no separate
 * domino dependency). The private export may disappear in future Angular
 * majors — so all access to it is confined to this file, and the regression
 * test (domino-adapter.spec.ts) fails on upgrade if the contract breaks.
 *
 * Fallback strategy: if the adapter is unavailable, server code can supply
 * its own DOM implementation via `globalThis.document` BEFORE the first
 * access to runtime-env (see resolveServerDocument in node.ts) — the global
 * document always takes priority over domino.
 */
import * as platformServer from '@angular/platform-server';

export type DominoAdapter = {
  makeCurrent(): void;
  new (): {
    getDefaultDocument(): Document;
  };
};

const DOMINO_ADAPTER_EXPORT = 'ɵDominoAdapter';

export const resolveDominoAdapter = (): DominoAdapter | undefined => {
  const platformServerRecord = platformServer as unknown as Record<string, unknown>;
  const adapter = platformServerRecord[DOMINO_ADAPTER_EXPORT];
  return adapter as DominoAdapter | undefined;
};

export const isDominoAdapterAvailable = (): boolean =>
  resolveDominoAdapter() !== undefined;

export const createDominoDocument = (): Document => {
  const adapter = resolveDominoAdapter();
  if (!adapter) {
    throw new Error(
      'RuntimeEnv: private ɵDominoAdapter not found in @angular/platform-server. ' +
        'Angular likely removed domino from platform-server in an update. ' +
        'Options: (1) provide globalThis.document before runtime-env initialization, ' +
        '(2) switch the node environment to another DOM emulation (linkedom/happy-dom).',
    );
  }
  adapter.makeCurrent();
  const instance = new adapter();
  return instance.getDefaultDocument();
};
